/**
 * i18n bootstrap.
 *
 * MoldOp supports Portuguese (pt, default for the target market) and
 * English (en, fallback + developer language). Language is selected via
 * i18next-browser-languagedetector which inspects, in order:
 *   1. localStorage key `moldop_lang`   (user preference)
 *   2. navigator.language               (browser default)
 *   3. 'pt'                              (fallback)
 *
 * Namespaces are declared per module so bundles can be code-split later
 * if the resource files grow too large. For now all namespaces ship
 * eagerly — the JSON totals well under 50 KB.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import ptCommon from './locales/pt/common.json';
import ptAuth from './locales/pt/auth.json';
import ptMaintenance from './locales/pt/maintenance.json';
import ptInjection from './locales/pt/injection.json';
import ptQuality from './locales/pt/quality.json';

import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enMaintenance from './locales/en/maintenance.json';
import enInjection from './locales/en/injection.json';
import enQuality from './locales/en/quality.json';

export const AVAILABLE_LANGUAGES = ['pt', 'en'] as const;
export type Language = (typeof AVAILABLE_LANGUAGES)[number];

export const LANG_STORAGE_KEY = 'moldop_lang';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      pt: {
        common: ptCommon,
        auth: ptAuth,
        maintenance: ptMaintenance,
        injection: ptInjection,
        quality: ptQuality,
      },
      en: {
        common: enCommon,
        auth: enAuth,
        maintenance: enMaintenance,
        injection: enInjection,
        quality: enQuality,
      },
    },
    fallbackLng: 'pt',
    supportedLngs: [...AVAILABLE_LANGUAGES],
    defaultNS: 'common',
    ns: ['common', 'auth', 'maintenance', 'injection', 'quality'],
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANG_STORAGE_KEY,
      caches: ['localStorage'],
    },
  });

export default i18n;
